import { callAPI } from './apiUtils';
import { apiUrls } from './apiUrls';
import { buildOptions, fitWithin, prepareLogoFile, MAX_LOGO_SIDE } from './customerDisplay';
import { sendCustomerDisplayOptions, isCustomerDisplayOptionsAvailable } from './customerDisplayBridge';

/**
 * Customer (second) screen settings. The state is per RESTAURANT and lives on the server
 * (get-customer-screen); this module reads it, edits the logo, and relays the effective logo to the
 * Android wrapper. localStorage only holds the last good answer so an offline cold start can still
 * relay the last known logo - it is never the source of truth.
 */

export const CUSTOMER_SCREEN_CACHE_KEY = 'customer_display_screen_cache';

export const getCachedCustomerScreen = () => {
  try {
    const raw = localStorage.getItem(CUSTOMER_SCREEN_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

const cacheCustomerScreen = (data) => {
  try {
    localStorage.setItem(CUSTOMER_SCREEN_CACHE_KEY, JSON.stringify(data));
  } catch (e) {
    /* cache only */
  }
};

export const clearCustomerScreenCache = () => {
  try {
    localStorage.removeItem(CUSTOMER_SCREEN_CACHE_KEY);
  } catch (e) {
    /* cache only */
  }
};

const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

const relay = (data) => sendCustomerDisplayOptions(buildOptions(data));

/** Cache and relay a server answer (used after fetch, upload and remove). */
export const applyCustomerScreen = (data) => {
  if (data && typeof data === 'object') cacheCustomerScreen(data);
  relay(data);
  return data || null;
};

/** GET get-customer-screen -> its `data`. Throws on any failure (404 on an older server included). */
export const fetchCustomerScreen = async () => {
  const res = await callAPI(apiUrls.GET_CUSTOMER_SCREEN, {}, 'GET', null, {}, {}, false);
  const data = res?.data?.data;
  if (!data || typeof data !== 'object') throw new Error('No customer screen settings in the response');
  return data;
};

/**
 * App start / after login: relay the cached answer straight away (so an offline start still shows
 * the last logo), then ask the server while online and relay the fresh answer. Any failure keeps
 * what was already relayed; with no cache at all the wrapper gets the bundled icon. A no-op unless
 * the wrapper exposes setCustomerDisplayOptions. Never throws.
 */
export const relayCustomerScreen = async () => {
  try {
    if (!isCustomerDisplayOptionsAvailable()) return null;
    const cached = getCachedCustomerScreen();
    // Nothing cached: wait for the server's answer rather than flashing the bundled icon first.
    if (cached) relay(cached);
    const fallBack = () => {
      if (!cached) relay(null);
      return cached;
    };
    if (!isOnline() || !localStorage.getItem('accessToken')) return fallBack();
    try {
      return applyCustomerScreen(await fetchCustomerScreen());
    } catch (e) {
      return fallBack();
    }
  } catch (e) {
    return null;
  }
};

/** Human text for a failed write: the server's own message first (permission denied etc.). */
export const customerScreenErrorMessage = (err, fallback = 'Could not save the logo. Please try again.') => {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim()) return serverMessage.trim();
  if (status === 403) return 'You do not have permission to change this setting.';
  if (!err?.response && !isOnline()) return 'You are offline. Connect to the internet to change the logo.';
  return fallback;
};

/** Browser implementation of the injected downscale step: <= 800 px long side, PNG stays PNG (transparency), otherwise JPEG q0.85. */
export const downscaleImageFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, MAX_LOGO_SIDE);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        const png = file.type === 'image/png';
        if (!png) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode image'))),
          png ? 'image/png' : 'image/jpeg',
          png ? undefined : 0.85
        );
      } catch (e) {
        reject(e);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });

/** Validate + downscale + POST multipart `logo`. Resolves to the new get-customer-screen answer, already cached and relayed. */
export const uploadCustomerScreenLogo = async (file, downscale = downscaleImageFile) => {
  const blob = await prepareLogoFile(file, downscale);
  const form = new FormData();
  form.append('logo', blob, blob.type === 'image/png' ? 'logo.png' : 'logo.jpg');
  const res = await callAPI(apiUrls.UPLOAD_CUSTOMER_SCREEN_LOGO, {}, 'POST', form, {});
  const data = res?.data?.data;
  if (res?.data?.status !== undefined && Number(res.data.status) !== 200 && Number(res.data.status) !== 1) {
    throw Object.assign(new Error(res.data.message || 'Upload failed'), { response: res });
  }
  return applyCustomerScreen(data && typeof data === 'object' ? data : await fetchCustomerScreen());
};

/** POST remove-customer-screen-logo -> back to the Business details logo. */
export const removeCustomerScreenLogo = async () => {
  const res = await callAPI(apiUrls.REMOVE_CUSTOMER_SCREEN_LOGO, {}, 'POST', {}, {});
  const data = res?.data?.data;
  if (res?.data?.status !== undefined && Number(res.data.status) !== 200 && Number(res.data.status) !== 1) {
    throw Object.assign(new Error(res.data.message || 'Remove failed'), { response: res });
  }
  return applyCustomerScreen(data && typeof data === 'object' ? data : await fetchCustomerScreen());
};
